/* Atlas V3 Desktop — Markets (the consolidated Universe desk).
   DeskSwitcher: UNIVERSE · LEVERAGE · GLOBAL. The Universe desk mirrors the
   mobile Markets pipeline exactly: live for EVERY range now — snapshot mode
   for 1D, historical mode (change1w/1m/6m/1y) for 5D/1M/6M/1Y, for the full
   universe (fixed 2026-08-22, audit finding F3 — this used to read the
   cron-filled stock_updates columns for non-1D ranges, which could disagree
   with the ticker sheet's live number for the same range). The nightly
   columns are now only the instant-paint fallback while a range's live fetch
   is in flight. Historical mode is cheap to fetch at universe scale because
   /api/prices computes its anchor closes from the OWNED price_history table,
   not per-ticker Polygon calls — see api/prices.js's fetchHistBatch.
   Row sparklines are the batched windowed /api/sparks?range shape (whole
   universe, one call, no row cap — audit F11) with a 1D intraday overlay on
   the visible rows, same wiring as mobile. LIST + treemap MAP views (shared
   MarketTreemap — no fork), scope chips, sector filter, sort flip, SHOW ALL
   expander, and a Leaders & Laggards band with real HeroCandles. */
import { useMemo, useState } from 'react'
import { StockLogo } from '@/v2/components/StockLogo'
import { intent, useTicker, Chip, Spark, Dropdown } from '@/v2/v3/lib'
import { HeroCandles } from '@/v2/v3/HeroCandles'
import { MarketTreemap } from '@/v2/v3/Markets'
import { RANGE_CONFIG } from '@/v2/lib/treemap'
import { useAtlasStocks } from '@/v2/hooks/useAtlasStocks'
import { useLivePrices } from '@/v2/hooks/useLivePrices'
import { useStockUpdates, useSparks } from '@/v2/hooks/useSupabaseData'
import { useTickerBars } from '@/v2/hooks/useChartBars'
import { useLeveragedEtfs } from '@/v2/hooks/useLeveragedEtfs'
import { useAdrUniverse } from '@/v2/hooks/useAdrUniverse'
import { useForeignQuotes } from '@/v2/hooks/useForeignQuotes'
import { Crossfade, SkelRowD, SkelText, SkelChart, enterStyle } from '../qol'

const Mono = { fontFamily: 'var(--studio-font-mono)' }
const Display = { fontFamily: 'var(--studio-font-display)', fontStyle: 'italic' }
const RANGES = ['1D', '5D', '1M', '6M', '1Y']
// Maps a timeframe chip to the LIVE prices field for that window (snapshot
// exposes change1d; historical mode exposes change1w/1m/6m/1y).
const LIVE_FIELD = { '1D': 'change1d', '5D': 'change1w', '1M': 'change1m', '6M': 'change6m', '1Y': 'change1y' }
const ROW_CAP = 50

function pct(v) { return v == null ? '—' : `${v >= 0 ? '+' : ''}${v.toFixed(2)}%` }
function fmtMcap(m) {
  if (!m) return ''
  const b = m / 1000
  return b >= 1 ? `$${b.toFixed(b >= 100 ? 0 : 1)}B` : `$${Math.round(m)}M`
}

export function Markets() {
  const [desk, setDesk] = useState('universe')
  return (
    <>
      <div className="v3web-phead">
        <div className="h" style={{ fontStyle: 'italic' }}>Markets</div>
      </div>
      <div className="v3web-desks">
        {[['universe', 'UNIVERSE'], ['leverage', 'LEVERAGE'], ['global', 'GLOBAL']].map(([k, l]) => (
          <button key={k} className={`v3web-desk ${desk === k ? 'on' : ''}`} onClick={() => setDesk(k)}>{l}</button>
        ))}
      </div>
      {desk === 'universe' && <UniverseDesk />}
      {desk === 'leverage' && <LeverageDeskView />}
      {desk === 'global' && <GlobalDeskView />}
    </>
  )
}

/* Top gainer / loser hero cell with a real candlestick — the desk idiom. */
function BandCell({ row, up, tf, onOpen }) {
  const c = up ? '--studio-success' : '--studio-danger'
  return (
    <button onClick={() => onOpen(row.ticker)}
      style={{ flex: 1, minWidth: 0, padding: '14px 18px', cursor: 'pointer', textAlign: 'left', background: 'transparent', border: 'none', borderLeft: up ? 'none' : '1px solid hsl(var(--studio-border))' }}>
      <div style={{ ...Mono, fontSize: 8.5, letterSpacing: '0.14em', textTransform: 'uppercase', color: `hsl(var(${c}))`, display: 'flex', alignItems: 'center', gap: 5 }}>
        {up ? '▲' : '▼'} {up ? 'Top gainer' : 'Top loser'} · {tf}
      </div>
      <div style={{ ...Display, fontSize: 30, lineHeight: 1, color: `hsl(var(${c}))`, marginTop: 8 }}>{pct(row.change)}</div>
      <div style={{ display: 'flex', alignItems: 'baseline', gap: 8, marginTop: 6, minWidth: 0 }}>
        <span style={{ ...Mono, fontSize: 12.5, color: 'hsl(var(--studio-text))' }}>{row.ticker}</span>
        <span style={{ fontSize: 11, color: 'hsl(var(--studio-text-subtle))', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{row.name}</span>
      </div>
      <div style={{ marginTop: 7 }}><HeroCandles ticker={row.ticker} range={tf} w={170} h={38} /></div>
    </button>
  )
}

function UniverseDesk() {
  const openTicker = useTicker()
  const { stocks, loading } = useAtlasStocks({ includeInactive: true })
  // Open on the main-tier MAP by default. The ALL scope (whole ~375-name
  // universe incl. gems) is hidden for now — its treemap is too dense to read;
  // the scope filter logic + the 'all' branch are left intact so it's a
  // one-line restore (re-add the ALL chip below).
  const [scope, setScope] = useState('main')
  const [tf, setTf] = useState('1D')
  const [sort, setSort] = useState('gain')
  const [sector, setSector] = useState('all')
  const [view, setView] = useState('map')
  const [drillSector, setDrillSector] = useState(null)
  const [expanded, setExpanded] = useState(false)

  const cfg = RANGE_CONFIG[tf] || RANGE_CONFIG['1D']

  // Live for every range — snapshot mode for 1D, historical for 5D/1M/6M/1Y
  // (mirrors mobile's fetch discipline exactly).
  const tickers = useMemo(() => stocks.map((s) => s.ticker), [stocks])
  const { prices: live } = useLivePrices(tickers, 60_000, tf === '1D' ? 'snapshot' : 'historical')
  const { updates } = useStockUpdates()
  // Windowed sparklines for the selected tf — one batched call over the whole
  // universe, no row cap (audit F11). 1D gets an intraday overlay below.
  const { sparks } = useSparks(tf)

  const updatesByTicker = useMemo(() => {
    const m = new Map()
    for (const u of (updates || [])) {
      const cur = m.get(u.ticker)
      if (!cur || (u.updated_at && (!cur.updated_at || u.updated_at > cur.updated_at))) m.set(u.ticker, u)
    }
    return m
  }, [updates])

  const sectors = useMemo(() => [...new Set(stocks.map((s) => s.sector).filter(Boolean))].sort(), [stocks])

  // One derived pipeline — the band, the list, and the map all read `rows`.
  // Live value for the selected range, with the matching nightly stock_updates
  // column as the instant-paint fallback while that range's live fetch loads.
  const liveField = LIVE_FIELD[tf] || 'change1d'
  const rows = useMemo(() => {
    const out = []
    for (const s of stocks) {
      if (scope === 'main' && s.tier !== 'main') continue
      if (scope === 'gems' && s.tier !== 'hunting') continue
      if (sector !== 'all' && s.sector !== sector) continue
      const u = updatesByTicker.get(s.ticker)
      const lv = live[s.ticker]
      const change = typeof lv?.[liveField] === 'number' ? lv[liveField]
        : (typeof u?.[cfg.field] === 'number' ? u[cfg.field] : null)
      const price = typeof lv?.price === 'number' ? lv.price : typeof u?.price === 'number' ? u.price : null
      out.push({ ticker: s.ticker, name: s.name || s.ticker, sector: s.sector || null, tier: s.tier, mcap: Number(s.market_cap_usd_m) || 0, change, price })
    }
    return out
  }, [stocks, scope, sector, liveField, cfg.field, live, updatesByTicker])

  const sorted = useMemo(() => {
    const list = rows.slice()
    list.sort((a, b) => {
      if (a.change == null && b.change == null) return a.ticker.localeCompare(b.ticker)
      if (a.change == null) return 1
      if (b.change == null) return -1
      return sort === 'loss' ? a.change - b.change : b.change - a.change
    })
    return list
  }, [rows, sort])

  let lead = null, lag = null
  for (const r of rows) {
    if (r.change == null) continue
    if (!lead || r.change > lead.change) lead = r
    if (!lag || r.change < lag.change) lag = r
  }
  if (lead && lead === lag) lag = null

  const pickScope = (id) => { setScope(id); setExpanded(false); setDrillSector(null) }
  const pickSector = (v) => { setSector(v); setExpanded(false); setDrillSector(null) }
  const pickView = (v) => { setView(v); if (v === 'list') setDrillSector(null) }

  const visible = expanded ? sorted : sorted.slice(0, ROW_CAP)

  // 1D-only intraday overlay for the visible rows (daily price_history can't
  // express an intraday shape); bounded to on-screen rows so it can't fan out.
  // Every other range is fully covered by the batched windowed sparks above,
  // no cap — see mobile Markets header + audit F11.
  const sparkTickers = useMemo(
    () => (tf === '1D' ? visible.slice(0, ROW_CAP).map((r) => r.ticker) : []),
    [visible, tf],
  )
  const { barsByTicker } = useTickerBars(sparkTickers, '1D')

  return (
    <>
      {view === 'list' && !lead && !lag && loading && (
        <div style={{ display: 'flex', marginBottom: 18, background: 'hsl(var(--studio-surface))', border: '1px solid hsl(var(--studio-border))', borderRadius: 'var(--studio-radius-lg)', overflow: 'hidden', maxWidth: 560 }}>
          {[0, 1].map((i) => (
            <div key={i} style={{ flex: 1, padding: '14px 18px', display: 'flex', flexDirection: 'column', gap: 9, borderLeft: i ? '1px solid hsl(var(--studio-border))' : 'none' }}>
              <SkelText w={104} h={9} />
              <SkelText w={96} h={26} />
              <SkelText w={130} h={10} />
              <SkelChart height={38} />
            </div>
          ))}
        </div>
      )}
      {view === 'list' && (lead || lag) && (
        <div className="v3web-enter" style={{ display: 'flex', marginBottom: 18, background: 'hsl(var(--studio-surface))', border: '1px solid hsl(var(--studio-border))', borderRadius: 'var(--studio-radius-lg)', overflow: 'hidden', maxWidth: 560 }}>
          {lead && <BandCell row={lead} up tf={tf} onOpen={openTicker} />}
          {lag && <BandCell row={lag} up={false} tf={tf} onOpen={openTicker} />}
        </div>
      )}

      {/* controls: scope + view chips · sector filter · timeframe + sort flip */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 14, marginBottom: 4 }}>
        <div style={{ display: 'flex', gap: 4 }}>
          {[['main', 'MAIN'], ['gems', 'GEMS']].map(([k, l]) => (
            <Chip key={k} active={scope === k} onClick={() => pickScope(k)}>{l}</Chip>
          ))}
        </div>
        <div style={{ display: 'flex', gap: 4 }}>
          <Chip active={view === 'list'} onClick={() => pickView('list')}>LIST</Chip>
          <Chip active={view === 'map'} onClick={() => pickView('map')}>MAP</Chip>
        </div>
        <span style={{ marginLeft: 'auto', display: 'inline-flex', alignItems: 'center', gap: 12 }}>
          <Dropdown value={sector} onChange={pickSector} align="right" size={9} fallbackLabel="All sectors" minWidth={190} maxHeight={340}
            options={[{ value: 'all', label: 'All sectors' }, ...sectors.map((s) => ({ value: s, label: s }))]} />
          <Dropdown value={tf} onChange={setTf} align="right" size={9} minWidth={120} options={RANGES.map((k) => ({ value: k, label: k }))} />
          {view === 'list' && (
            <button onClick={() => setSort((s) => (s === 'gain' ? 'loss' : 'gain'))} aria-label="Toggle sort direction"
              style={{ background: 'none', border: 'none', padding: 0, cursor: 'pointer', color: 'hsl(var(--studio-accent))', fontSize: 13, lineHeight: 1 }}>
              {sort === 'loss' ? '↑' : '↓'}
            </button>
          )}
        </span>
      </div>
      {view === 'map' && (
        <div style={{ ...Mono, fontSize: 9.5, letterSpacing: '0.06em', color: 'hsl(var(--studio-text-subtle))', margin: '2px 0 8px' }}>
          {drillSector
            ? <button onClick={() => setDrillSector(null)} style={{ all: 'unset', cursor: 'pointer', color: 'hsl(var(--studio-accent))' }}>‹ {String(drillSector).toUpperCase()} — BACK TO ALL SECTORS</button>
            : <>Color = {cfg.label} move · size = market cap · click a sector to zoom</>}
        </div>
      )}

      {view === 'list' ? (
        <div>
          {sorted.length === 0 && !loading && <div className="v3web-empty"><div className="pe-k">No names match this filter</div></div>}
          {loading && rows.length === 0 && (
            <Crossfade loading skeleton={<div>{[0, 1, 2, 3, 4, 5, 6, 7].map((i) => <SkelRowD key={i} i={i} />)}</div>} />
          )}
          {visible.map((r, ri) => {
            const up = (r.change ?? 0) >= 0
            return (
              <div key={r.ticker} className="v3web-row" onClick={() => openTicker(r.ticker)} style={enterStyle(ri)}>
                <StockLogo ticker={r.ticker} size={26} />
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div className="nm">{r.ticker} <span style={{ color: 'hsl(var(--studio-text-subtle))', fontSize: 12 }}>{r.name}</span>
                    {scope === 'all' && r.tier === 'hunting' && <span style={{ ...Mono, fontSize: 7.5, letterSpacing: '0.1em', color: 'hsl(var(--studio-accent))', border: '1px solid hsl(var(--studio-accent) / 0.5)', borderRadius: 3, padding: '1px 3px', marginLeft: 6 }}>GEM</span>}
                  </div>
                  <div className="sub">{r.sector}{r.mcap ? ` · ${fmtMcap(r.mcap)}` : ''}</div>
                </div>
                <Spark data={(tf === '1D' ? barsByTicker[r.ticker] : null) ?? sparks[r.ticker] ?? null} up={up} width={56} height={22} />
                {r.price != null && <div className="v3web-num" style={{ fontSize: 12, color: 'hsl(var(--studio-text-muted))', width: 80, textAlign: 'right' }}>${Number(r.price).toFixed(2)}</div>}
                <div className="v3web-num" style={{ fontSize: 13, color: r.change == null ? 'hsl(var(--studio-text-subtle))' : `hsl(var(${intent(up)}))`, width: 80, textAlign: 'right' }}>{pct(r.change)}</div>
              </div>
            )
          })}
          {!expanded && sorted.length > ROW_CAP && (
            <button onClick={() => setExpanded(true)} style={{ width: '100%', padding: '15px 20px', background: 'transparent', border: 'none', borderTop: '1px solid hsl(var(--studio-border) / 0.35)', cursor: 'pointer', ...Mono, fontSize: 10, letterSpacing: '0.12em', color: 'hsl(var(--studio-accent))' }}>
              SHOW ALL {sorted.length} NAMES
            </button>
          )}
        </div>
      ) : (
        <div style={{ height: 'min(70vh, 640px)', borderRadius: 'var(--studio-radius)', overflow: 'hidden', border: '1px solid hsl(var(--studio-border) / 0.5)' }}>
          <MarketTreemap rows={rows} cfg={cfg} onTicker={openTicker} drillSector={drillSector} onDrill={setDrillSector} loading={loading && rows.length === 0} />
        </div>
      )}
    </>
  )
}

const LEV_TFS = [['1D', 'change1d'], ['5D', 'change1w'], ['1M', 'change1m'], ['6M', 'change6m'], ['1Y', 'change1y']]
function LeverageDeskView() {
  const openTicker = useTicker()
  const { instruments, loading: levLoading } = useLeveragedEtfs()
  const [tf, setTf] = useState('1D')
  const field = (LEV_TFS.find(([l]) => l === tf) || LEV_TFS[0])[1]
  const etfTickers = useMemo(() => instruments.map((i) => i.etfs?.[0]?.ticker).filter(Boolean), [instruments])
  const { prices } = useLivePrices(etfTickers, 60_000, 'historical')
  // Real per-ETF sparks matching the selected timeframe (was the fixed
  // ~10-day /api/sparks shape on the UNDERLYING, not even the ETF the
  // move/price columns show — both mismatches fixed together).
  const { barsByTicker } = useTickerBars(etfTickers, tf)

  const rows = useMemo(() => instruments
    .map((inst) => {
      const etf = inst.etfs?.[0]
      const move = etf ? prices[etf.ticker]?.[field] : null
      const px = etf ? prices[etf.ticker]?.price : null
      return { inst, etf, move, px }
    })
    .sort((a, b) => (b.move ?? -Infinity) - (a.move ?? -Infinity)), [instruments, prices, field])

  return (
    <>
      <div style={{ display: 'flex', gap: 6, marginBottom: 14 }}>
        {LEV_TFS.map(([l]) => <Chip key={l} active={tf === l} onClick={() => setTf(l)}>{l}</Chip>)}
      </div>
      <div className="v3web-list">
        {rows.length === 0 && levLoading && (
          <Crossfade loading skeleton={<div>{[0, 1, 2, 3, 4, 5].map((i) => <SkelRowD key={i} i={i} spark={false} />)}</div>} />
        )}
        {rows.map(({ inst, etf, move, px }, ri) => {
          const up = (move ?? 0) >= 0
          return (
            <div key={inst.underlying} className="v3web-row" onClick={() => openTicker(inst.underlying)} style={enterStyle(ri)}>
              <StockLogo ticker={inst.underlying} size={26} />
              <div style={{ flex: 1, minWidth: 0 }}>
                <div className="nm">{inst.underlying} <span style={{ color: 'hsl(var(--studio-text-subtle))', fontSize: 12 }}>{inst.name}</span></div>
                <div className="sub">{etf ? `${etf.ticker} · ${etf.leverage || '2×'}${etf.issuer ? ' · ' + etf.issuer : ''}` : inst.sector}</div>
              </div>
              <Spark data={etf ? barsByTicker[etf.ticker] : null} up={up} width={56} height={22} />
              {px != null && <div className="v3web-num" style={{ fontSize: 12, color: 'hsl(var(--studio-text-muted))', width: 80, textAlign: 'right' }}>${Number(px).toFixed(2)}</div>}
              <div className="v3web-num" style={{ fontSize: 13, color: move == null ? 'hsl(var(--studio-text-subtle))' : `hsl(var(${intent(up)}))`, width: 80, textAlign: 'right' }}>{pct(move)}</div>
            </div>
          )
        })}
      </div>
    </>
  )
}

function GlobalDeskView() {
  const openTicker = useTicker()
  const { us, home, preipo, counts, loading: adrLoading } = useAdrUniverse()
  const [tab, setTab] = useState('us')
  const usTickers = useMemo(() => us.map((s) => s.ticker), [us])
  // Snapshot mode: the tab shows the 1D move; snapshot returns change1d
  // fast + reliably (historical is slow and often empty for the ADR set).
  const { prices } = useLivePrices(tab === 'us' ? usTickers : [])
  // Real per-ticker 1D sparks to match the 1D % this tab always shows (was
  // the fixed ~10-day /api/sparks shape).
  const { barsByTicker } = useTickerBars(tab === 'us' ? usTickers : [], '1D')
  const { quotes } = useForeignQuotes(tab === 'home')

  return (
    <>
      <div style={{ display: 'flex', gap: 18, marginBottom: 16 }}>
        {[['us', `US-Listed`, counts.us], ['home', 'Home Market', counts.home], ['preipo', 'Pre-IPO', counts.preipo]].map(([k, l, n]) => (
          <button key={k} onClick={() => setTab(k)} style={{ all: 'unset', cursor: 'pointer', ...Mono, fontSize: 11, letterSpacing: '0.1em', paddingBottom: 8, borderBottom: tab === k ? '1.5px solid hsl(var(--studio-accent))' : '1.5px solid transparent', color: tab === k ? 'hsl(var(--studio-accent))' : 'hsl(var(--studio-text-muted))' }}>{l} <span style={{ color: 'hsl(var(--studio-text-subtle))' }}>{n}</span></button>
        ))}
      </div>

      {tab === 'us' && (
        <div className="v3web-list">
          {us.length === 0 && adrLoading && (
            <Crossfade loading skeleton={<div>{[0, 1, 2, 3, 4, 5].map((i) => <SkelRowD key={i} i={i} spark={false} />)}</div>} />
          )}
          {us.map((s, ri) => {
            const c = prices[s.ticker]?.change1d; const p = prices[s.ticker]?.price; const up = (c ?? 0) >= 0
            return (
              <div key={s.ticker} className="v3web-row" onClick={() => openTicker(s.ticker)} style={enterStyle(ri)}>
                <StockLogo ticker={s.ticker} size={26} />
                <div style={{ flex: 1, minWidth: 0 }}><div className="nm">{s.ticker} <span style={{ color: 'hsl(var(--studio-text-subtle))', fontSize: 12 }}>{s.company}</span></div><div className="sub">{s.sector}</div></div>
                <Spark data={barsByTicker[s.ticker]} up={up} width={56} height={22} />
                {p != null && <div className="v3web-num" style={{ fontSize: 12, color: 'hsl(var(--studio-text-muted))', width: 80, textAlign: 'right' }}>${Number(p).toFixed(2)}</div>}
                <div className="v3web-num" style={{ fontSize: 13, color: c == null ? 'hsl(var(--studio-text-subtle))' : `hsl(var(${intent(up)}))`, width: 80, textAlign: 'right' }}>{pct(c)}</div>
              </div>
            )
          })}
        </div>
      )}

      {tab === 'home' && (
        <div className="v3web-list">
          {home.length === 0 && adrLoading && (
            <Crossfade loading skeleton={<div>{[0, 1, 2, 3, 4, 5].map((i) => <SkelRowD key={i} i={i} spark={false} />)}</div>} />
          )}
          {home.map((row) => {
            const q = quotes[row.ticker]
            const usd = q?.usd ?? row.last_price_usd
            const local = q?.local ?? row.last_price_local
            return (
              <div key={row.ticker} className="v3web-row" style={{ cursor: 'default' }}>
                <StockLogo ticker={row.ticker} size={26} />
                <div style={{ flex: 1, minWidth: 0 }}><div className="nm">{row.ticker} <span style={{ color: 'hsl(var(--studio-text-subtle))', fontSize: 12 }}>{row.company}</span></div><div className="sub">{row.sector}{row.exchange ? ` · ${row.exchange}` : ''}</div></div>
                <div className="v3web-num" style={{ fontSize: 12, color: 'hsl(var(--studio-text-muted))', textAlign: 'right' }}>
                  {local != null && <div>{Number(local).toLocaleString()} {row.last_ccy || ''}</div>}
                  {usd != null && <div style={{ color: 'hsl(var(--studio-text-subtle))', fontSize: 11 }}>${Number(usd).toFixed(2)}</div>}
                </div>
              </div>
            )
          })}
        </div>
      )}

      {tab === 'preipo' && (
        <div className="v3web-list">
          {preipo.map((row) => (
            <div key={row.ticker || row.company} className="v3web-row" style={{ cursor: 'default' }}>
              <div style={{ flex: 1, minWidth: 0 }}><div className="nm">{row.company}</div><div className="sub">{row.sector}{row.status ? ` · ${row.status}` : ''}{row.expected_listing ? ` · ${row.expected_listing}` : ''}</div></div>
              {row.valuation && <div className="v3web-num" style={{ fontSize: 12, color: 'hsl(var(--studio-text-muted))' }}>{row.valuation}</div>}
            </div>
          ))}
        </div>
      )}
    </>
  )
}
